import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Wallet, TrendingUp, Target, PiggyBank } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend } from "recharts";

const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

const COLORS = ["#17365D", "#E5B52A", "#2E7D65", "#B45309", "#8B4A9E", "#0F766E", "#A61B47"];

const toneForPercent = (pct) => {
  if (pct >= 90) return "success";
  if (pct >= 50) return "info";
  if (pct >= 20) return "warning";
  return "destructive";
};

export default function AnggaranRealisasi() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);
  const [drillBidang, setDrillBidang] = useState(null); // filter for program tab
  const [activeTab, setActiveTab] = useState("bidang");

  const load = async () => {
    setLoading(true);
    try {
      const q = year === "ALL" ? "" : `?year=${year}`;
      const res = await api.get(`/finance/budget-realisasi${q}`);
      setData(res.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year]);

  const totalPct = data?.totals?.percent ?? 0;
  const totalToneClass = totalPct >= 80 ? "text-emerald-700" : totalPct >= 40 ? "text-amber-700" : "text-rose-700";

  const bidangChart = useMemo(() => {
    if (!data) return [];
    return data.by_bidang.slice(0, 8).map((b) => ({
      name: b.key,
      Anggaran: b.budget,
      Realisasi: b.realisasi,
    }));
  }, [data]);

  const bagianChart = useMemo(() => {
    if (!data) return [];
    return data.by_bagian.map((b) => ({
      name: b.key.replace("_", " "),
      value: b.budget,
    }));
  }, [data]);

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Keuangan • Anggaran</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <Wallet className="h-7 w-7 text-secondary" /> Anggaran vs Realisasi
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Pantau serapan dana program per Bidang Organisasi, 4 Bagian Pelayanan, dan detail per program.
          </p>
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-40" data-testid="select-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["ALL", "2026", "2025"].map(y => <SelectItem key={y} value={y}>{y === "ALL" ? "Semua Tahun" : `Tahun ${y}`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : !data ? null : (
        <>
          {/* Total KPI */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-5">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground font-semibold flex items-center gap-1"><Target className="h-3 w-3" /> Total Anggaran</div>
                <div className="font-heading font-bold text-2xl text-primary mt-1">{fmtIDR(data.totals.budget)}</div>
                <div className="text-xs text-muted-foreground mt-1">{data.totals.count} program</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground font-semibold flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Realisasi</div>
                <div className={`font-heading font-bold text-2xl mt-1 ${totalToneClass}`}>{fmtIDR(data.totals.realisasi)}</div>
                <div className="text-xs text-muted-foreground mt-1">Terpakai dari anggaran</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground font-semibold flex items-center gap-1"><PiggyBank className="h-3 w-3" /> Sisa</div>
                <div className="font-heading font-bold text-2xl text-emerald-700 mt-1">{fmtIDR(data.totals.budget - data.totals.realisasi)}</div>
                <div className="text-xs text-muted-foreground mt-1">Tersedia untuk pelayanan</div>
              </CardContent>
            </Card>
            <Card className="bg-primary/5 border-primary/30">
              <CardContent className="p-5">
                <div className="text-[0.65rem] tracking-widest uppercase text-primary font-semibold">Serapan</div>
                <div className={`font-heading font-bold text-3xl mt-1 ${totalToneClass}`}>{totalPct}%</div>
                <Progress value={totalPct} className="h-1.5 mt-2" />
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="bidang" data-testid="tab-bidang">Per Bidang Organisasi ({data.by_bidang.length})</TabsTrigger>
              <TabsTrigger value="bagian" data-testid="tab-bagian">Per 4 Bagian Pelayanan</TabsTrigger>
              <TabsTrigger value="program" data-testid="tab-program">Per Program ({data.by_program.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="bidang">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <Card className="lg:col-span-3">
                  <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                    <CardTitle className="font-heading text-base">Anggaran vs Realisasi per Bidang</CardTitle>
                    <div className="text-[0.65rem] text-muted-foreground">Klik bar untuk drill ke program</div>
                  </CardHeader>
                  <CardContent className="p-4 pt-0" style={{ height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bidangChart} onClick={(state) => {
                        if (state?.activeLabel) {
                          setDrillBidang(state.activeLabel);
                          setActiveTab("program");
                        }
                      }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="name" fontSize={11} style={{ cursor: "pointer" }} />
                        <YAxis fontSize={10} tickFormatter={(v) => `${(v/1000000).toFixed(0)}jt`} />
                        <Tooltip formatter={(v) => fmtIDR(v)} />
                        <Bar dataKey="Anggaran" fill="#17365D" radius={[3,3,0,0]} style={{ cursor: "pointer" }} />
                        <Bar dataKey="Realisasi" fill="#E5B52A" radius={[3,3,0,0]} style={{ cursor: "pointer" }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Ranking Bidang</CardTitle></CardHeader>
                  <CardContent className="p-4 pt-0 space-y-2 max-h-[320px] overflow-y-auto">
                    {data.by_bidang.map((b, i) => (
                      <button
                        key={b.key}
                        type="button"
                        onClick={() => { setDrillBidang(b.key); setActiveTab("program"); }}
                        className="w-full text-left border-b border-border pb-2 last:border-0 hover:bg-muted/50 rounded p-1 -mx-1 transition-colors"
                        data-testid={`row-bidang-${b.key}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                            <div className="text-sm font-medium truncate">{b.label}</div>
                          </div>
                          <StatusBadge status={toneForPercent(b.percent)} label={`${b.percent}%`} />
                        </div>
                        <div className="text-[0.7rem] text-muted-foreground mt-1 flex items-center justify-between">
                          <span>{fmtIDR(b.realisasi)} / {fmtIDR(b.budget)}</span>
                          <span>{b.count} prog · drill →</span>
                        </div>
                        <Progress value={b.percent} className="h-1 mt-1" />
                      </button>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="bagian">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Alokasi Anggaran per Bagian</CardTitle></CardHeader>
                  <CardContent className="p-4 pt-0" style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={bagianChart} dataKey="value" nameKey="name" outerRadius={100} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                          {bagianChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtIDR(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <div className="space-y-3">
                  {data.by_bagian.map((b, i) => (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => {
                        // Filter program tab by the first child bidang of this bagian (proxy)
                        setDrillBidang(b.child_bidangs?.[0] || b.key);
                        setActiveTab("program");
                      }}
                      className="w-full text-left"
                      data-testid={`card-bagian-${b.key}`}
                    >
                      <Card className="hover:border-primary/40 transition-colors">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                                <div className="font-heading font-bold text-sm truncate">{b.label}</div>
                              </div>
                              <div className="text-[0.7rem] text-muted-foreground mt-1">{b.count} program • {b.child_bidangs.join(", ")}</div>
                            </div>
                            <StatusBadge status={toneForPercent(b.percent)} label={`${b.percent}%`} />
                          </div>
                          <Progress value={b.percent} className="h-2 my-2" />
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Realisasi <strong className="text-foreground">{fmtIDR(b.realisasi)}</strong></span>
                            <span className="text-muted-foreground">dari <strong className="text-foreground">{fmtIDR(b.budget)}</strong></span>
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="program">
              {drillBidang && (
                <div className="mb-3 flex items-center gap-2" data-testid="drill-filter-chip">
                  <span className="text-xs text-muted-foreground">Difilter dari drill-down:</span>
                  <span className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                    Bidang: {drillBidang}
                    <button onClick={() => setDrillBidang(null)} className="text-primary/70 hover:text-primary" data-testid="btn-clear-drill">✕</button>
                  </span>
                </div>
              )}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="text-left p-3 font-semibold">Program</th>
                          <th className="text-left p-3 font-semibold">Bidang</th>
                          <th className="text-right p-3 font-semibold">Anggaran</th>
                          <th className="text-right p-3 font-semibold">Realisasi</th>
                          <th className="text-left p-3 font-semibold w-64">Serapan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(drillBidang ? data.by_program.filter(p => (p.bidang || "").toUpperCase() === drillBidang.toUpperCase()) : data.by_program).map((p) => (
                          <tr key={p.id} className="border-t border-border" data-testid={`row-program-${p.id}`}>
                            <td className="p-3">
                              <div className="font-medium">{p.name}</div>
                              {p.status && <div className="text-[0.7rem] text-muted-foreground">{p.status}</div>}
                            </td>
                            <td className="p-3 text-xs">{p.bidang || "—"}</td>
                            <td className="p-3 text-right whitespace-nowrap">{fmtIDR(p.budget)}</td>
                            <td className="p-3 text-right whitespace-nowrap">{fmtIDR(p.realisasi)}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Progress value={p.percent} className="h-2 flex-1" />
                                <StatusBadge status={toneForPercent(p.percent)} label={`${p.percent}%`} />
                              </div>
                            </td>
                          </tr>
                        ))}
                        {drillBidang && data.by_program.filter(p => (p.bidang || "").toUpperCase() === drillBidang.toUpperCase()).length === 0 && (
                          <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">Tidak ada program di bidang <strong>{drillBidang}</strong>.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </AppShell>
  );
}
